import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'

import type { CustomerGroup } from '@/lib/api/customer-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    useCreateCustomerGroup,
    useCustomerGroups,
    useDeleteCustomerGroup,
    useUpdateCustomerGroup,
} from '@/hooks/use-customer-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/customers/groups/',
)({
    component: CustomerGroupsPage,
})

const schema = z.object({
    name: z.string().min(1, m.customer_group_name_required()),
    description: z.string().optional(),
    discount_percent: z.coerce.number().min(0).max(100),
})

type FormData = z.infer<typeof schema>

function CustomerGroupsPage() {
    const { data: groups = [], isLoading } = useCustomerGroups()
    const createMutation = useCreateCustomerGroup()
    const updateMutation = useUpdateCustomerGroup()
    const deleteMutation = useDeleteCustomerGroup()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingGroup, setEditingGroup] = useState<CustomerGroup | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<CustomerGroup | null>(null)

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', description: '', discount_percent: 0 },
    })

    const openCreate = () => {
        setEditingGroup(null)
        form.reset({ name: '', description: '', discount_percent: 0 })
        setDialogOpen(true)
    }

    const openEdit = (group: CustomerGroup) => {
        setEditingGroup(group)
        form.reset({
            name: group.name,
            description: group.description ?? '',
            discount_percent: group.discount_percent,
        })
        setDialogOpen(true)
    }

    const handleSubmit = form.handleSubmit(async (data) => {
        if (editingGroup) {
            await updateMutation.mutateAsync({
                id: editingGroup.id,
                data: {
                    name: data.name,
                    description: data.description || undefined,
                    discount_percent: data.discount_percent,
                },
            })
        } else {
            await createMutation.mutateAsync({
                name: data.name,
                description: data.description || undefined,
                discount_percent: data.discount_percent,
            })
        }
        setDialogOpen(false)
    })

    const handleDelete = async () => {
        if (!deleteTarget) return
        await deleteMutation.mutateAsync(deleteTarget.id)
        setDeleteTarget(null)
    }

    const isPending = createMutation.isPending || updateMutation.isPending

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>{m.customer_group_page_title()}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            {m.customer_group_page_desc()}
                        </p>
                    </div>
                    <Button size="sm" onClick={openCreate}>
                        <Plus className="size-4 mr-1" />
                        {m.customer_group_add()}
                    </Button>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : groups.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">
                            {m.customer_group_empty()}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        {m.customer_group_name_label()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_group_desc_label()}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {m.customer_group_discount_label()}
                                    </TableHead>
                                    <TableHead className="w-24" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groups.map((g) => (
                                    <TableRow key={g.id}>
                                        <TableCell className="font-medium">
                                            <Link
                                                to="/customers/groups/$groupId"
                                                params={{ groupId: g.id }}
                                                className="text-primary hover:underline"
                                            >
                                                {g.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {g.description || '—'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {g.discount_percent}%
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 justify-end">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => openEdit(g)}
                                                >
                                                    <Pencil className="size-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="text-destructive"
                                                    onClick={() =>
                                                        setDeleteTarget(g)
                                                    }
                                                >
                                                    <Trash2 className="size-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingGroup
                                ? m.customer_group_edit()
                                : m.customer_group_add()}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="group-name">
                                {m.customer_group_name_label()} *
                            </Label>
                            <Input
                                id="group-name"
                                {...form.register('name')}
                                autoFocus
                            />
                            {form.formState.errors.name && (
                                <p className="text-xs text-destructive">
                                    {form.formState.errors.name.message}
                                </p>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="group-desc">
                                {m.customer_group_desc_label()}
                            </Label>
                            <Input
                                id="group-desc"
                                {...form.register('description')}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="group-discount">
                                {m.customer_group_discount_label()} *
                            </Label>
                            <Input
                                id="group-discount"
                                type="number"
                                min={0}
                                max={100}
                                step={0.1}
                                {...form.register('discount_percent')}
                            />
                            {form.formState.errors.discount_percent && (
                                <p className="text-xs text-destructive">
                                    {
                                        form.formState.errors.discount_percent
                                            .message
                                    }
                                </p>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDialogOpen(false)}
                            >
                                {m.common_cancel()}
                            </Button>
                            <Button type="submit" disabled={isPending}>
                                {isPending
                                    ? m.common_saving()
                                    : m.common_save()}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.customer_group_delete_confirm()}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        {deleteTarget?.name}
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteMutation.isPending}
                        >
                            {m.common_delete()}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
